import { Analytics } from "@vercel/analytics/react";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { strainData } from "../data/strainData";
import { terpeneData } from "../data/terpeneData";
import { supabase } from "../lib/supabase";
import { useEffect } from "react";


/* PRIORITY */
const priorityWeights = {
  "Acute (Severe / Immediate)": 3,
  "Chronic (Ongoing)": 2,
  "Occasional (Infrequent)": 1
};

const priorityOptions = [
  "Acute (Severe / Immediate)",
  "Chronic (Ongoing)",
  "Occasional (Infrequent)"
];

/* 🎨 TERP COLORS */
const terpColors = {
  Limonene: "text-yellow-400",
  Myrcene: "text-purple-400",
  Caryophyllene: "text-red-400",
  Pinene: "text-green-400",
  Linalool: "text-pink-400",
  Humulene: "text-orange-400",
  Terpinolene: "text-blue-400",
  Ocimene: "text-cyan-400",
  Bisabolol: "text-rose-300"
};

/* CONDITIONS */
const conditionsData = {
  Anxiety: {
    icon: "😌",
    terpenes: {
      Linalool: 4,      // calming, anti-anxiety
      Limonene: 3,      // mood elevation
      Caryophyllene: 2  // stress + inflammation link
    }
  },

  Depression: {
    icon: "🌧️",
    terpenes: {
      Limonene: 4,      // mood boosting
      Pinene: 2,        // alertness
      Terpinolene: 2    // uplifting/energizing
    }
  },

  PTSD: {
    icon: "🧠",
    terpenes: {
      Linalool: 4,      // anxiety + sedation
      Caryophyllene: 3, // emotional regulation (CB2)
      Myrcene: 2        // body relaxation
    }
  },

  Pain: {
    icon: "💢",
    terpenes: {
      Caryophyllene: 4, // anti-inflammatory + pain
      Myrcene: 3,       // muscle relaxation
      Humulene: 2       // inflammation
    }
  },

  Inflammation: {
    icon: "🔥",
    terpenes: {
      Caryophyllene: 4,
      Humulene: 3,
      Bisabolol: 2      // soothing / anti-irritant
    }
  },

  Migraines: {
    icon: "🤕",
    terpenes: {
      Caryophyllene: 3,
      Linalool: 3,      // tension relief
      Pinene: 2         // possible vascular support
    }
  },

  Insomnia: {
    icon: "😴",
    terpenes: {
      Myrcene: 4,       // sedating
      Linalool: 3,
      Terpinolene: 1    // mild calming
    }
  },

  ADHD: {
    icon: "🎯",
    terpenes: {
      Pinene: 4,        // focus + memory
      Limonene: 2,      // mood + energy
      Terpinolene: 2    // stimulation
    }
  }
};


/* SYMPTOMS */
const symptomsData = {
  Stress: {
    icon: "😰",
    terpenes: {
      Linalool: 3,
      Caryophyllene: 2,
      Limonene: 2
    }
  },

  Fatigue: {
    icon: "🥱",
    terpenes: {
      Limonene: 3,
      Terpinolene: 2,
      Pinene: 2
    }
  },

  "Brain Fog": {
    icon: "🌫️",
    terpenes: {
      Pinene: 4,
      Limonene: 2
    }
  },

  "Trouble Sleeping": {
    icon: "🌙",
    terpenes: {
      Myrcene: 3,
      Linalool: 2,
      Caryophyllene: 1
    }
  },

  Nausea: {
    icon: "🤮",
    terpenes: {
      Limonene: 3,
      Caryophyllene: 2
    }
  },

  Tremors: {
  icon: "🫨",
  terpenes: {
      Myrcene: 4,       // muscle relaxation
      Linalool: 3,      // calming nervous system
     Caryophyllene: 3, // anti-inflammatory / CB2
     Pinene: 1         // clarity support
    }
  }
};

export default function Generator() {
  const [selections, setSelections] = useState(() => {
  const saved = localStorage.getItem("terpivo_selections");
  return saved ? JSON.parse(saved) : {};
});

const [selectedState, setSelectedState] = useState(() => {
  const saved = localStorage.getItem("terpivo_state");
  return saved || "ALL";
});
  const [products, setProducts] = useState([]);
  const [results, setResults] = useState(null);

  useEffect(() => {
  localStorage.setItem("terpivo_selections", JSON.stringify(selections));
}, [selections]);
const [showResetModal, setShowResetModal] = useState(false);
const [openSection, setOpenSection] = useState(null);
const [showAll, setShowAll] = useState(false);

const toggleSection = (section) => {
  setOpenSection(openSection === section ? null : section);
};

useEffect(() => {
  localStorage.setItem("terpivo_state", selectedState);
}, [selectedState]);

  const toggleItem = (item) => {
    if (selections[item]) {
      const updated = { ...selections };
      delete updated[item];
      setSelections(updated);
    } else {
      setSelections({ ...selections, [item]: "Chronic (Ongoing)" });
    }
  };

  const setPriority = (item, priority) => {
    setSelections({ ...selections, [item]: priority });
  };

  /* 🔥 GLOBAL TERP LOGIC */
const calculateTopTerps = () => {
  const terpeneScores = {};
  const allItems = { ...conditionsData, ...symptomsData };

  Object.entries(selections).forEach(([item, priority]) => {
    const weight = priorityWeights[priority] || 1;
    const data = allItems[item];

    if (!data?.terpenes) return;

    Object.entries(data.terpenes).forEach(([terp, score]) => {
      terpeneScores[terp] = (terpeneScores[terp] || 0) + score * weight;
    });
  });

  // 🔥 return BOTH terp + score (more powerful for later)
  return Object.entries(terpeneScores)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);
};

  /* 🔥 SORT TERPS BY % */
  const getTopTerpenes = (terpenes) => {
    if (!terpenes) return [];

    return Object.entries(terpenes)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3);
  };

  /* 🔥 SIMPLE FEELING */
  const getOverallEffect = (terpEntries) => {
  let profile = {
    energy: 0,
    focus: 0,
    calm: 0,
    sedation: 0,
    balance: 0
  };

  terpEntries.forEach(([terp, value]) => {
    const weight = value;

    if (["Limonene", "Terpinolene", "Ocimene"].includes(terp)) {
      profile.energy += weight;
    }

    if (terp === "Pinene") {
      profile.focus += weight;
    }

    if (["Linalool", "Humulene", "Bisabolol"].includes(terp)) {
      profile.calm += weight;
    }

    if (terp === "Myrcene") {
      profile.sedation += weight;
    }

    if (terp === "Caryophyllene") {
      profile.balance += weight;
    }
  });

  const { energy, focus, calm, sedation, balance } = profile;

  /* 🔥 BUILD HUMAN SENTENCE */

  if (energy > sedation && energy > calm) {
    return "uplifting, energetic, and mood boosting";
  }

  if (sedation > energy && sedation > focus) {
    return "deeply relaxing, body-heavy, and potentially sedating";
  }

  if (focus > sedation && focus > calm) {
    return "clear-headed, focused, and mentally sharp";
  }

  if (calm > energy && calm > focus) {
    return "calming, steady, and stress-relieving";
  }

  if (balance > 0.5) {
    return "balanced, smooth, and well-rounded";
  }

  return "balanced with a mix of mental and physical effects";
};

const formatList = (arr) => {
  if (!arr || arr.length === 0) return "";
  if (arr.length === 1) return arr[0];
  if (arr.length === 2) return `${arr[0]} & ${arr[1]}`;
  return `${arr.slice(0, -1).join(", ")}, & ${arr[arr.length - 1]}`;
};

  const generateRecommendation = async () => {
  const selectedItems = Object.keys(selections);

  // 🔥 get top terps
  const topWithScores = calculateTopTerps();
  const top = topWithScores.map(([t]) => t);

  // 🔥 get strains for selected state
  let stateStrains = [];

  if (selectedState === "ALL") {
    stateStrains = Object.values(strainData).flat();
  } else {
    stateStrains = strainData[selectedState] || [];
  }

  // 🔥 fallback
  if (!stateStrains.length) {
    setProducts([]);
    setResults({
      top: [],
      dispensaryTip: "No data available for this state yet."
    });
    return;
  }

  // 🔥 scoring
  const scored = stateStrains.map((s) => {
    let score = 0;

    Object.entries(s.terpenes || {}).forEach(([terp, value]) => {
      if (top.includes(terp)) {
        score += value * 10;
      }
    });

    const match = Math.min(100, Math.round(score * 10));

    return {
      ...s,
      match
    }; 
  });

const sortedMatches = scored.sort((a, b) => {
  if (b.match !== a.match) {
    return b.match - a.match;
  }

  const aHasLineage = a.lineage && a.lineage !== "Unknown";
  const bHasLineage = b.lineage && b.lineage !== "Unknown";

  if (aHasLineage !== bHasLineage) {
    return bHasLineage ? 1 : -1;
  }

  const aTerpCount = Object.keys(a.terpenes || {}).length;
  const bTerpCount = Object.keys(b.terpenes || {}).length;

  return bTerpCount - aTerpCount;
});

const bestMatches = sortedMatches.slice(0, 5);
  
    supabase.from("user_events").insert([
  {
    state: selectedState,
    conditions: selectedItems,
    top_terpenes: top,
    results: bestMatches.map((s) => s.strain)
  }
]).then(() => {
  console.log("Analytics sent");
}).catch((err) => {
  console.error("Analytics error:", err);
});


  // 🔥 formatting (uses global formatList function)
  const formattedConditions = formatList(selectedItems);
  const formattedTerps = formatList(top);

  // 🔥 messaging
  const dispensaryTip =
    selectedState === "ALL"
      ? `
I'm looking for something to help with ${formattedConditions}.

I'm especially interested in terpene profiles high in ${formattedTerps}.

Do you have anything similar to that?
`
      : `
I'm looking for something to help with ${formattedConditions}.

Ideally something high in ${formattedTerps}.
`;

  // 🔥 LIGHTWEIGHT ANALYTICS (for now)
  console.log("USER EVENT:", {
    state: selectedState,
    conditions: selectedItems,
    topTerpenes: top,
    results: bestMatches.map((s) => s.strain)
  });

  // 🔥 update UI
  setProducts(bestMatches);
setResults({
  top,
  dispensaryTip,
  allMatches: sortedMatches // 👈 ADD THIS
});
};

   const renderList = (data) =>
    Object.keys(data).map((item) => {
      const selected = selections[item];

      return (
        <motion.div
          key={item}
          whileHover={{ scale: 1.03 }}
          className={`p-3 mb-2 rounded-xl cursor-pointer ${
            selected ? "bg-green-500/30" : "bg-white/10"
          }`}
          onClick={() => toggleItem(item)}
        >
          <div className="text-center">
            {data[item].icon} {item}
          </div>

          {selected && (
            <div className="flex justify-center gap-2 mt-2 flex-wrap">
              {priorityOptions.map((p) => (
                <button
                  key={p}
                  onClick={(e) => {
                    e.stopPropagation();
                    setPriority(item, p);
                  }}
                  className={`px-2 py-1 text-xs rounded-full ${
                    selected === p ? "bg-green-500" : "bg-white/20"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </motion.div>
      );
    });

  return (
    <div className="bg-black text-white min-h-screen p-6">
 
      <h1 className="text-2xl font-bold mb-2 text-center flex items-center justify-center gap-3">
  {/* Left Logo */}
  <img
    src="/terpivogrey.png"
    alt="Terpivo Logo Left"
    className="w-8 h-8"
  />

  {/* Text */}
  <span>Terpivo Generator</span>

  {/* Right Logo */}
  <img
    src="/terpivogrey.png"
    alt="Terpivo Logo Right"
    className="w-8 h-8"
  />
</h1>


      <p className="text-sm opacity-70 mb-6 text-center max-w-xl mx-auto">
        Cannabis is more than just THC percentage. Terpenes play a major role in how a strain feels, influencing mood, energy, relaxation, and overall experience.

Select your conditions or symptoms below, and Terpivo will recommend strains based on terpene profiles—not just potency. Selecting your state improves recommendations but is not required.
      </p>

      {/* SELECTED ITEMS */}
{Object.keys(selections).length > 0 && (
  <div className="mb-6">

    <h3 className="text-sm opacity-70 text-center mb-2">
      Your Selections
    </h3>

    <div className="flex flex-wrap justify-center gap-2">

      {Object.entries(selections).map(([item, priority]) => (
        <div
          key={item}
          className="flex items-center gap-2 bg-green-500/20 px-3 py-1 rounded-full text-xs"
        >
          <span>
            {item}
          </span>

          {/* priority badge */}
          <span className="opacity-70">
            ({priority.split(" ")[0]})
          </span>

          {/* remove button */}
          <button
            onClick={() => toggleItem(item)}
            className="ml-1 text-white/60 hover:text-white"
          >
            ✕
          </button>
        </div>
      ))}

    </div>
  </div>
)}

      {/* CONDITIONS */}
<div className="mb-4">
  <button
    onClick={() => toggleSection("conditions")}
    className="w-full bg-white/20 hover:bg-white/30 p-4 rounded-xl text-center font-semibold transition"
  >
    Conditions {openSection === "conditions" ? "−" : "+"}
  </button>

  {openSection === "conditions" && (
    <div className="mt-2 space-y-2">
      {renderList(conditionsData)}
    </div>
  )}
</div>

{/* SYMPTOMS */}
<div className="mb-4">
  <button
    onClick={() => toggleSection("symptoms")}
    className="w-full bg-white/20 hover:bg-white/30 p-4 rounded-xl text-center font-semibold transition"
  >
    Symptoms {openSection === "symptoms" ? "−" : "+"}
  </button>

  {openSection === "symptoms" && (
    <div className="mt-2 space-y-2">
      {renderList(symptomsData)}
    </div>
  )}
</div>

<div className="mt-6 text-center">
  <label className="block mb-2 text-sm opacity-70">
    Select Your State
  </label>

  <div className="relative w-full">
    <select
      value={selectedState}
      onChange={(e) => setSelectedState(e.target.value)}
      className="bg-white text-black p-3 rounded-xl w-full text-center font-semibold appearance-none pr-8"
    >
      <option value="ALL">🌎 Not State Specific</option>
      <option value="AZ">Arizona (Recreational)</option>
      <option value="CA">California (Recreational)</option>
      <option value="CT">Connecticut (Recreational)</option>
      <option value="FL">Florida (Medical)</option>
      <option value="IL">Illinois (Recreational)</option>
      <option value="MD">Maryland (Recreational)</option>
      <option value="MA">Massachusetts (Recreational)</option>
      <option value="MI">Michigan (Recreational)</option>
      <option value="MN">Minnesota (Recreational)</option>
      <option value="MO">Missouri (Recreational)</option>
      <option value="NV">Nevada (Recreational)</option>
      <option value="NJ">New Jersey (Recreational)</option>
      <option value="NY">New York (Recreational)</option>
      <option value="OH">Ohio (Recreational)</option>
      <option value="PA">Pennsylvania (Medical)</option>
      <option value="RI">Rhode Island (Recreational)</option>
      <option value="VA">Virginia (Medical)</option>
    </select>

    {/* Custom arrow */}
    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-black text-sm">
      ▼
    </div>
  </div>
</div>

<button  
  onClick={generateRecommendation}
  className="mt-4 bg-green-500 p-3 rounded-xl w-full"
>
  Generate Recommendation
</button>

<div className="flex justify-center mt-3">
  <button
    onClick={() => setShowResetModal(true)}
    className="text-xs px-3 py-1 rounded-full border border-white/20 hover:bg-white/10 transition opacity-60 hover:opacity-100"
  >
    Reset Selections
  </button>
</div>

      {results && (
        <div className="mt-6 bg-blue-500/20 p-4 rounded-2xl">
          <h2>🧠 Top Recommended Terpenes</h2>

          {results.top.map((t) => {
            const terp = terpeneData[t];

            return (
              <div key={t} className="mt-2 text-sm">
                <span className={`font-semibold ${terpColors[t]}`}>
                  {t}
                </span>
                <div>{terp?.description}</div>
                <div className="text-xs opacity-70">Mental: {terp?.mental}</div>
                <div className="text-xs opacity-70">Physical: {terp?.physical}</div>
              </div>
            );
          })}
        </div>
      )}

     {/* MATCHING STRAINS */}
{products.length > 0 && (
  <div className="mt-6 bg-white/10 p-4 rounded-2xl">
    <h2>🛒 Matching Strains</h2>

    {products.map((p) => {
      const topTerps = getTopTerpenes(p.terpenes);

      return (
        <div key={p.id} className="mb-4 border-b border-white/10 pb-3">

          <p className="font-semibold">
            🌿 {p.strain} ({p.type})
          </p>

          <p className="text-sm opacity-70">
            THC: {p.thc}% | Match: {p.match}% | Lineage: {p.lineage && p.lineage !== "Unknown" ? p.lineage : "N/A"}
          </p>

          <div className="text-sm mt-1">
            {topTerps.map(([t, v], i) => (
              <span key={t} className={terpColors[t]}>
                {t} ({v.toFixed(2)}%) {i < 2 && " • "}
              </span>
            ))}
          </div>

          <p className="text-sm mt-2 opacity-80">
            Overall, this creates a {getOverallEffect(topTerps)} experience.
          </p>

        </div>
      );
    })}

    {/* 🔥 SHOW ALL BUTTON */}
    {results?.allMatches?.length > 5 && (
      <div className="text-center mt-4">
        <button
          onClick={() => setShowAll(!showAll)}
          className="text-sm px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 transition"
        >
          {showAll ? "Show Top Results Only" : "Show All Matches"}
        </button>
      </div>
    )}
  </div>
)}

{/* 🔥 FULL LIST */}
{showAll && results?.allMatches && (
  <div className="mt-6 bg-white/5 p-4 rounded-2xl">
    <h2 className="mb-4">📊 All Matching Strains (Ranked)</h2>

    {results.allMatches.map((p, index) => {
      return (
        <div key={p.id} className="mb-3 border-b border-white/10 pb-2">

          <p className="text-sm font-semibold">
            #{index + 1} — {p.strain}
          </p>

          <p className="text-xs opacity-70">
            Match: {p.match}% | THC: {p.thc}% | {p.lineage && p.lineage !== "Unknown" ? p.lineage : "N/A"}
          </p>

        </div>
      );
    })}
  </div>
)}

{/* WHAT TO SAY */}
{results && (
  <div className="bg-green-500/20 p-4 rounded-2xl mt-6">
    <h2>📍 What to Say</h2>
    <p className="text-sm whitespace-pre-line">
      {results.dispensaryTip}
    </p>
  </div>
)}

      <p className="text-xs opacity-50 mt-10 text-center max-w-xl mx-auto">
        Disclaimer: Terpivo provides general recommendations based on terpene profiles and user input. This is not medical advice. Always consult with a qualified healthcare professional for medical concerns.
         *Some states display their terpene data differently. Recommendations generated may not be identical to local dispensaries.
      </p>
<AnimatePresence>
  {showResetModal && (
    <motion.div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >

      <motion.div
        className="bg-black border border-white/20 rounded-2xl p-6 w-[90%] max-w-sm text-center space-y-4"
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.25 }}
      >

        <h2 className="text-lg font-semibold">
          Reset Selections?
        </h2>

        <p className="text-sm opacity-70">
          This will clear all selections and results.
        </p>

        <div className="flex gap-3 justify-center mt-4">

          {/* Cancel */}
          <button
            onClick={() => setShowResetModal(false)}
            className="px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 transition"
          >
            Cancel
          </button>

          {/* Confirm */}
          <button
            onClick={() => {
              setSelections({});
              setSelectedState("ALL");
              setProducts([]);
              setResults(null);

              localStorage.removeItem("terpivo_selections");
              localStorage.removeItem("terpivo_state");

              setShowResetModal(false);
            }}
            className="px-4 py-2 rounded-xl bg-red-500 hover:bg-red-600 transition"
          >
            Reset
          </button>

        </div>

      </motion.div>
    </motion.div>
  )}
</AnimatePresence>


      <Analytics />
    </div>
  );
}